// Aug-2026 — Phase 1: Project Scope & BOQ tab (opened from the Projects list).
// Excel import → server validation preview → Draft commit → maker/checker
// submit → versioned baseline. Commercial columns render ONLY when the
// backend says commercials_visible (server-side stripping is the real gate).
import { useCallback, useEffect, useState } from "react";
import { api, formatINR, formatDate, downloadFile } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Upload, Download, FileSpreadsheet, Send, X, RefreshCw, Eye,
  GitCompareArrows, History, ShieldAlert, FileDown, CheckCircle2,
  Plus, Pencil, Trash2, CopyPlus,
} from "lucide-react";
import { SearchableSelect } from "@/components/SearchableSelect";

const V_BADGE = {
  Draft: "bg-amber-100 text-amber-800 border-amber-300",
  Submitted: "bg-sky-100 text-sky-800 border-sky-300",
  Approved: "bg-emerald-100 text-emerald-800 border-emerald-300",
  Rejected: "bg-rose-100 text-rose-800 border-rose-300",
  Superseded: "bg-slate-200 text-slate-600 border-slate-300",
};
const ROW_BADGE = {
  Valid: "bg-emerald-100 text-emerald-800",
  Warning: "bg-amber-100 text-amber-800",
  Rejected: "bg-rose-100 text-rose-800",
  SkippedGuidance: "bg-slate-100 text-slate-500",
  SkippedExample: "bg-slate-100 text-slate-500",
};

async function blobDownload(path, filename) {
  try {
    const res = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    downloadFile(url, filename);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) {
    let msg = "Download failed";
    try { msg = JSON.parse(await e.response.data.text())?.detail || msg; } catch { /* not json */ }
    toast.error(msg);
  }
}

const Badge = ({ status }) => (
  <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold border rounded ${V_BADGE[status] || "bg-slate-100 text-slate-600 border-slate-300"}`}>
    {status}
  </span>
);

const Tile = ({ label, value, testid }) => (
  <div className="bg-white border border-slate-200 rounded px-3 py-2" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className="text-sm font-bold text-slate-900 tabular-nums">{value}</div>
  </div>
);

function SummaryTiles({ summary, commercial }) {
  if (!summary) return null;
  return (
    <div className="flex flex-wrap gap-2" data-testid="boq-summary-tiles">
      <Tile label="Lines" value={summary.line_count ?? 0} testid="boq-tile-lines" />
      <Tile label="Measurable" value={summary.measurable_count ?? 0} testid="boq-tile-measurable" />
      <Tile label="Lumpsum" value={summary.lumpsum_count ?? 0} testid="boq-tile-lumpsum" />
      <Tile label="Descriptive" value={summary.descriptive_count ?? 0} testid="boq-tile-descriptive" />
      <Tile label="Rejected" value={summary.rejected_count ?? 0} testid="boq-tile-rejected" />
      {commercial && (
        <>
          <Tile label="Scope Value (ex GST)" value={formatINR(summary.scope_value_ex_gst || 0)} testid="boq-tile-exgst" />
          <Tile label="GST" value={formatINR(summary.gst_value || 0)} testid="boq-tile-gst" />
          <Tile label="Scope Value (incl GST)" value={formatINR(summary.scope_value_inc_gst || 0)} testid="boq-tile-incgst" />
          <Tile label="Budget Value" value={formatINR(summary.budget_value || 0)} testid="boq-tile-budget" />
        </>
      )}
    </div>
  );
}

function UploadPreview({ projectId, preview, onCommitted, onDiscarded }) {
  const [busy, setBusy] = useState(false);
  const commercial = !!preview.commercials_visible;
  const shown = (preview.rows || []).slice(0, 300);

  const doCommit = async (extra = {}) => {
    setBusy(true);
    try {
      const r = await api.post(`/projects/${projectId}/boq/uploads/${preview.upload_id}/commit`, extra);
      toast.success(`Draft ${r.data.boq_number} created — ${r.data.lines} line(s)`);
      onCommitted();
    } catch (e) {
      const msg = e?.response?.data?.detail
        || (e?.response?.status ? `Commit failed (HTTP ${e.response.status}) — please retry or refresh the BOQ tab` : "Commit failed — network error, please retry");
      if (e?.response?.status === 409 && /rejected/i.test(msg) && !extra.accept_partial) {
        if (window.confirm(`${msg}\n\nCommit only the VALID rows?`)) { setBusy(false); return doCommit({ ...extra, accept_partial: true }); }
      } else if (e?.response?.status === 409 && /already imported/i.test(msg) && !extra.allow_duplicate) {
        if (window.confirm(`${msg}\n\nDeliberately create a NEW draft version from this duplicate file?`)) { setBusy(false); return doCommit({ ...extra, allow_duplicate: true }); }
      } else toast.error(msg);
    } finally { setBusy(false); }
  };

  const doDiscard = async () => {
    try {
      await api.post(`/projects/${projectId}/boq/uploads/${preview.upload_id}/discard`);
      toast.success("Upload discarded");
      onDiscarded();
    } catch (e) { toast.error(e?.response?.data?.detail || "Discard failed"); }
  };

  return (
    <div className="border border-sky-300 bg-sky-50/50 rounded-lg p-4 space-y-3" data-testid="boq-upload-preview">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-bold text-slate-900">
          Validation Preview — <span className="font-mono text-xs">{preview.file_name}</span>
        </div>
        <div className="flex gap-2">
          <button data-testid="boq-exception-btn" disabled={busy}
            onClick={() => blobDownload(`/projects/${projectId}/boq/uploads/${preview.upload_id}/exceptions`, "boq_exceptions.xlsx")}
            className="px-3 py-1.5 text-xs font-bold border border-amber-400 text-amber-800 bg-amber-50 rounded hover:bg-amber-100 inline-flex items-center gap-1">
            <FileDown size={13} /> Exception Report
          </button>
          <button data-testid="boq-discard-btn" disabled={busy} onClick={doDiscard}
            className="px-3 py-1.5 text-xs font-bold border border-slate-300 text-slate-700 bg-white rounded hover:bg-slate-50 inline-flex items-center gap-1">
            <X size={13} /> Discard
          </button>
          <button data-testid="boq-commit-btn" disabled={busy} onClick={() => doCommit({})}
            className="px-3 py-1.5 text-xs font-bold bg-[#0B1F3A] text-white rounded hover:bg-[#13294f] inline-flex items-center gap-1">
            <CheckCircle2 size={13} /> Create Draft Version
          </button>
        </div>
      </div>
      {preview.duplicate_warning && (
        <div className="text-xs text-amber-800 bg-amber-100 border border-amber-300 rounded px-3 py-2" data-testid="boq-duplicate-warning">
          {preview.duplicate_message}
        </div>
      )}
      <SummaryTiles summary={preview.summary} commercial={commercial} />
      <div className="text-[11px] text-slate-500">
        Skipped automatically: {preview.skipped?.guidance || 0} guidance row(s), {preview.skipped?.example || 0} example row(s), {preview.skipped?.blank || 0} blank row(s).
        {(preview.rows || []).length > 300 && ` Showing first 300 of ${(preview.rows || []).length} rows.`}
      </div>
      <div className="overflow-x-auto max-h-96 overflow-y-auto border border-slate-200 rounded bg-white">
        <table className="w-full min-w-[760px] text-xs" data-testid="boq-preview-rows">
          <thead className="sticky top-0 bg-slate-100">
            <tr className="text-left text-[10px] uppercase tracking-wider text-slate-600">
              <th className="px-2 py-1.5">Row</th>
              <th className="px-2 py-1.5">Status</th>
              <th className="px-2 py-1.5">Name</th>
              <th className="px-2 py-1.5">Category</th>
              <th className="px-2 py-1.5">UOM</th>
              <th className="px-2 py-1.5 text-right">Qty</th>
              {commercial && <th className="px-2 py-1.5 text-right">Client Rate</th>}
              {commercial && <th className="px-2 py-1.5 text-right">Tax %</th>}
              <th className="px-2 py-1.5">Type</th>
              <th className="px-2 py-1.5">Messages</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r, i) => (
              <tr key={i} className="border-t border-slate-100" data-testid={`boq-preview-row-${r.excel_row}`}>
                <td className="px-2 py-1 tabular-nums text-slate-500">{r.excel_row}</td>
                <td className="px-2 py-1">
                  <span className={`inline-block px-1.5 py-0.5 text-[9px] uppercase font-bold rounded ${ROW_BADGE[r.status] || "bg-slate-100 text-slate-600"}`}>{r.status}</span>
                </td>
                <td className="px-2 py-1 max-w-[220px] truncate" title={r.name}>{r.name}</td>
                <td className="px-2 py-1">{r.category_display || ""}</td>
                <td className="px-2 py-1">{r.uom_name || ""}</td>
                <td className="px-2 py-1 text-right tabular-nums">{r.quantity ?? ""}</td>
                {commercial && <td className="px-2 py-1 text-right tabular-nums">{r.client_rate ?? ""}</td>}
                {commercial && <td className="px-2 py-1 text-right tabular-nums">{r.tax_percent ?? ""}</td>}
                <td className="px-2 py-1 text-[10px]">{r.line_type || ""}</td>
                <td className="px-2 py-1 max-w-[320px] truncate text-slate-600" title={(r.messages || []).join(" · ")}>
                  {(r.messages || []).join(" · ")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// UX-1 — manual BOQ line editor (Draft versions only; server re-validates)
const TAX_OPTS = ["", "0", "3", "5", "6", "9", "12", "18", "28"];

function LineEditorModal({ projectId, versionId, existing, commercial, onClose, onSaved }) {
  const [cats, setCats] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    name: existing?.name || "", description: existing?.description || "",
    category: existing?.category_display || "", uom: existing?.uom_name || "",
    scope_status: existing?.client_scope_status || "Approved",
    brand_make: existing?.brand_make || "", item_type: existing?.item_type || "",
    quantity: existing?.quantity ?? "", hsn: existing?.hsn || "",
    client_rate: existing?.client_rate ?? "", budget_rate: existing?.budget_rate ?? "",
    tax_percent: existing?.tax_percent ?? "",
  });
  useEffect(() => {
    api.get(`/projects/${projectId}/boq/categories`)
      .then(r => setCats(r.data.categories || [])).catch(() => {});
    api.get("/uom-master", { params: { limit: 500 } })
      .then(r => setUoms((Array.isArray(r.data) ? r.data : []).map(u => u.name))).catch(() => {});
  }, [projectId]);
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const save = async () => {
    const payload = {
      name: f.name, description: f.description, category: f.category,
      uom: f.uom, scope_status: f.scope_status, brand_make: f.brand_make,
      item_type: f.item_type, hsn: f.hsn,
      quantity: f.quantity === "" ? null : Number(f.quantity),
    };
    if (commercial) {
      payload.client_rate = f.client_rate === "" ? null : Number(f.client_rate);
      payload.budget_rate = f.budget_rate === "" ? null : Number(f.budget_rate);
      payload.tax_percent = f.tax_percent === "" ? null : Number(f.tax_percent);
    }
    setBusy(true);
    try {
      if (existing) await api.put(`/projects/${projectId}/boq/versions/${versionId}/lines/${existing.id}`, payload);
      else await api.post(`/projects/${projectId}/boq/versions/${versionId}/lines`, payload);
      toast.success(existing ? "Line updated" : "Line added");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const inp = "border border-slate-300 rounded px-2 py-1.5 text-xs w-full";
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto py-10" data-testid="boq-line-editor">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="text-sm font-bold text-slate-900">
            {existing ? `Edit line ${existing.element_code}` : "Add BOQ Line"}
            <span className="ml-2 text-[10px] text-slate-500 font-normal">Draft version only — server re-validates every field</span>
          </div>
          <button data-testid="boq-line-editor-close" onClick={onClose} className="text-slate-500 hover:text-slate-800"><X size={16} /></button>
        </div>
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
          <label className="col-span-2">Name *
            <input data-testid="boq-le-name" className={inp} value={f.name} onChange={e => set("name", e.target.value)} /></label>
          <label className="col-span-2">Description *
            <textarea data-testid="boq-le-desc" className={`${inp} h-14`} value={f.description} onChange={e => set("description", e.target.value)} /></label>
          <label>Category *
            <SearchableSelect testid="boq-le-category"
              items={cats.map(c => ({ id: c.display, name: c.display }))}
              value={f.category} onChange={(id) => set("category", id)}
              placeholder="— search category —" /></label>
          <label>UOM *
            <SearchableSelect testid="boq-le-uom"
              items={uoms.map(u => ({ id: u, name: u }))}
              value={f.uom} onChange={(id) => set("uom", id)}
              placeholder="— search UOM —" /></label>
          <label>Client Scope Status *
            <select data-testid="boq-le-scope" className={inp} value={f.scope_status} onChange={e => set("scope_status", e.target.value)}>
              <option value="Approved">Approved</option>
              <option value="Not Approved">Not Approved</option>
            </select></label>
          <label>Quantity
            <input data-testid="boq-le-qty" type="number" min="0" step="any" className={inp} value={f.quantity} onChange={e => set("quantity", e.target.value)} /></label>
          <label>Brand / Make
            <input data-testid="boq-le-brand" className={inp} value={f.brand_make} onChange={e => set("brand_make", e.target.value)} /></label>
          <label>HSN
            <input data-testid="boq-le-hsn" className={inp} value={f.hsn} onChange={e => set("hsn", e.target.value)} /></label>
          {commercial && (
            <>
              <label>Client Rate (₹)
                <input data-testid="boq-le-rate" type="number" min="0" step="any" className={inp} value={f.client_rate} onChange={e => set("client_rate", e.target.value)} /></label>
              <label>Budget Rate (₹)
                <input data-testid="boq-le-budget" type="number" min="0" step="any" className={inp} value={f.budget_rate} onChange={e => set("budget_rate", e.target.value)} /></label>
              <label>Tax %
                <select data-testid="boq-le-tax" className={inp} value={String(f.tax_percent)} onChange={e => set("tax_percent", e.target.value)}>
                  {TAX_OPTS.map(t => <option key={t} value={t}>{t === "" ? "— blank —" : t}</option>)}
                </select></label>
            </>
          )}
          {!commercial && (
            <div className="col-span-2 text-[10px] text-slate-500 bg-slate-50 border border-slate-200 rounded px-2 py-1">
              Commercial fields (rates / tax) require the BOQ Commercials permission — lines saved without them are stored as descriptive.
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200">
          <button onClick={onClose} className="px-3 py-1.5 text-xs font-bold text-slate-600 border border-slate-300 rounded hover:bg-slate-50">Cancel</button>
          <button data-testid="boq-le-save" disabled={busy} onClick={save}
            className="px-4 py-1.5 text-xs font-bold text-white bg-[#0B1F3A] rounded hover:bg-[#13294f] disabled:opacity-50">
            {busy ? "Saving…" : existing ? "Save Line" : "Add Line"}
          </button>
        </div>
      </div>
    </div>
  );
}

function LinesPanel({ projectId, version, onClose, onDataChanged }) {
  const { hasPerm } = useAuth();
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [refresh, setRefresh] = useState(0);
  const [editorFor, setEditorFor] = useState(null); // {line} | {add:true}
  const isDraft = version.status === "Draft";
  const canAdd = isDraft && hasPerm("project_boq", "create");
  const canManage = isDraft && hasPerm("project_boq", "edit");
  const canScope = hasPerm("project_boq", "edit") && (isDraft || version.status === "Approved");
  useEffect(() => {
    api.get(`/projects/${projectId}/boq/versions/${version.id}/lines`, { params: { page, limit: 50 } })
      .then(r => setData(r.data)).catch(() => setData({ rows: [], total: 0 }));
  }, [projectId, version.id, page, refresh]);
  const changed = () => { setEditorFor(null); setRefresh(x => x + 1); onDataChanged?.(); };
  const delLine = async (l) => {
    const reason = window.prompt(`Delete line ${l.element_code} — ${l.name}?\nOptional reason (recorded in version history):`);
    if (reason === null) return;
    try {
      await api.delete(`/projects/${projectId}/boq/versions/${version.id}/lines/${l.id}`, { params: { reason } });
      toast.success("Line deleted (Draft-only controlled operation)");
      changed();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  const flipScope = async (l, scope) => {
    try {
      await api.put(`/projects/${projectId}/boq/versions/${version.id}/lines/${l.id}/client-scope`, { client_scope_status: scope });
      toast.success(`${l.element_code} client scope → ${scope} (audited)`);
      changed();
    } catch (e) { toast.error(e?.response?.data?.detail || "Client scope update failed"); }
  };
  const commercial = !!data?.commercials_visible;
  const pages = Math.max(1, Math.ceil((data?.total || 0) / 50));
  return (
    <div className="border border-slate-300 rounded-lg bg-white p-4 space-y-2" data-testid="boq-lines-panel">
      <div className="flex items-center justify-between">
        <div className="text-sm font-bold text-slate-900">Lines — {version.boq_number} <Badge status={version.status} /></div>
        <div className="flex items-center gap-2">
          {canAdd && (
            <button data-testid="boq-add-line-btn" onClick={() => setEditorFor({ add: true })}
              className="px-2 py-1 text-xs font-bold border border-emerald-400 text-emerald-800 bg-emerald-50 rounded hover:bg-emerald-100 inline-flex items-center gap-1">
              <Plus size={12} /> Add Line
            </button>
          )}
          <button data-testid="boq-lines-close" onClick={onClose} className="text-slate-500 hover:text-slate-800"><X size={16} /></button>
        </div>
      </div>
      {editorFor && (
        <LineEditorModal projectId={projectId} versionId={version.id}
          existing={editorFor.line || null} commercial={commercial}
          onClose={() => setEditorFor(null)} onSaved={changed} />
      )}
      <div className="hidden md:block overflow-x-auto max-h-96 overflow-y-auto border border-slate-200 rounded">
        <table className="w-full min-w-[880px] text-xs" data-testid="boq-lines-table">
          <thead className="sticky top-0 bg-slate-100">
            <tr className="text-left text-[10px] uppercase tracking-wider text-slate-600">
              <th className="px-2 py-1.5">Element Code</th>
              <th className="px-2 py-1.5">Name</th>
              <th className="px-2 py-1.5">Category</th>
              <th className="px-2 py-1.5">UOM</th>
              <th className="px-2 py-1.5 text-right">Qty</th>
              {commercial && <><th className="px-2 py-1.5 text-right">Client Rate</th><th className="px-2 py-1.5 text-right">Tax %</th><th className="px-2 py-1.5 text-right">Taxable</th><th className="px-2 py-1.5 text-right">GST</th><th className="px-2 py-1.5 text-right">Total</th><th className="px-2 py-1.5 text-right">Budget Rate</th></>}
              <th className="px-2 py-1.5">Type</th>
              <th className="px-2 py-1.5">Client Scope</th>
              {canManage && <th className="px-2 py-1.5">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {(data?.rows || []).map(l => (
              <tr key={l.id} className="border-t border-slate-100" data-testid={`boq-line-${l.element_code}`}>
                <td className="px-2 py-1 font-mono text-[11px] font-bold">{l.element_code}</td>
                <td className="px-2 py-1 max-w-[240px] truncate" title={l.name}>{l.name}</td>
                <td className="px-2 py-1">{l.category_display}</td>
                <td className="px-2 py-1">{l.uom_name}</td>
                <td className="px-2 py-1 text-right tabular-nums">{l.quantity ?? ""}</td>
                {commercial && <><td className="px-2 py-1 text-right tabular-nums">{l.client_rate ?? ""}</td><td className="px-2 py-1 text-right tabular-nums">{l.tax_percent ?? ""}</td><td className="px-2 py-1 text-right tabular-nums">{l.taxable_value != null ? formatINR(l.taxable_value) : ""}</td><td className="px-2 py-1 text-right tabular-nums">{l.gst_value != null ? formatINR(l.gst_value) : ""}</td><td className="px-2 py-1 text-right tabular-nums">{l.total_value != null ? formatINR(l.total_value) : ""}</td><td className="px-2 py-1 text-right tabular-nums">{l.budget_rate ?? ""}</td></>}
                <td className="px-2 py-1 text-[10px]">{l.line_type}{l.excluded ? " (non-valued)" : ""}</td>
                <td className="px-2 py-1 text-[10px]">
                  {canScope ? (
                    <select data-testid={`boq-line-scope-${l.element_code}`} value={l.client_scope_status || "Approved"}
                      onChange={e => flipScope(l, e.target.value)}
                      className={`border rounded px-1 py-0.5 text-[10px] font-bold ${l.client_scope_status === "Not Approved" ? "border-amber-400 text-amber-800 bg-amber-50" : "border-emerald-400 text-emerald-800 bg-emerald-50"}`}>
                      <option>Approved</option>
                      <option>Not Approved</option>
                    </select>
                  ) : (
                    <span className={`px-1.5 py-0.5 rounded font-bold ${l.client_scope_status === "Not Approved" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>{l.client_scope_status || "Approved"}</span>
                  )}
                </td>
                {canManage && (
                  <td className="px-2 py-1 whitespace-nowrap">
                    <button data-testid={`boq-line-edit-${l.element_code}`} onClick={() => setEditorFor({ line: l })}
                      className="text-slate-700 hover:underline mr-2 inline-flex items-center gap-0.5"><Pencil size={10} /> Edit</button>
                    <button data-testid={`boq-line-del-${l.element_code}`} onClick={() => delLine(l)}
                      className="text-rose-700 hover:underline inline-flex items-center gap-0.5"><Trash2 size={10} /> Del</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile: stacked line cards */}
      <div className="md:hidden max-h-96 overflow-y-auto border border-slate-200 rounded divide-y divide-slate-100" data-testid="boq-lines-cards">
        {(data?.rows || []).length === 0 && (
          <div className="px-3 py-5 text-center text-slate-400 text-xs">No lines.</div>
        )}
        {(data?.rows || []).map(l => (
          <div key={l.id} className="p-3 space-y-1" data-testid={`boq-line-card-${l.element_code}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-[11px] font-bold">{l.element_code}</span>
              <span className={`px-1.5 py-0.5 rounded font-bold text-[9px] uppercase ${l.client_scope_status === "Not Approved" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
                {l.client_scope_status || "Approved"}
              </span>
            </div>
            <div className="text-xs text-slate-800 break-words">{l.name}</div>
            <div className="text-[10px] text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5">
              <span>{l.category_display}</span>
              <span>{l.uom_name}</span>
              <span>Qty: <b>{l.quantity ?? "—"}</b></span>
              <span>{l.line_type}{l.excluded ? " (non-valued)" : ""}</span>
            </div>
            {commercial && (
              <div className="text-[10px] text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5">
                <span>Rate: {l.client_rate ?? "—"}</span>
                <span>Tax: {l.tax_percent ?? "—"}%</span>
                <span>Total: {l.total_value != null ? formatINR(l.total_value) : "—"}</span>
                <span>Budget: {l.budget_rate ?? "—"}</span>
              </div>
            )}
            {(canScope || canManage) && (
              <div className="flex flex-wrap items-center gap-3 pt-0.5">
                {canScope && (
                  <select data-testid={`boq-line-scope-m-${l.element_code}`} value={l.client_scope_status || "Approved"}
                    onChange={e => flipScope(l, e.target.value)}
                    className={`border rounded px-1 py-0.5 text-[10px] font-bold ${l.client_scope_status === "Not Approved" ? "border-amber-400 text-amber-800 bg-amber-50" : "border-emerald-400 text-emerald-800 bg-emerald-50"}`}>
                    <option>Approved</option>
                    <option>Not Approved</option>
                  </select>
                )}
                {canManage && (
                  <>
                    <button data-testid={`boq-line-edit-m-${l.element_code}`} onClick={() => setEditorFor({ line: l })}
                      className="text-slate-700 hover:underline inline-flex items-center gap-0.5 text-xs"><Pencil size={10} /> Edit</button>
                    <button data-testid={`boq-line-del-m-${l.element_code}`} onClick={() => delLine(l)}
                      className="text-rose-700 hover:underline inline-flex items-center gap-0.5 text-xs"><Trash2 size={10} /> Del</button>
                  </>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 text-xs text-slate-600">
        <span>Page {page} of {pages} · {data?.total || 0} lines</span>
        <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-2 py-0.5 border rounded disabled:opacity-40" data-testid="boq-lines-prev">Prev</button>
        <button disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="px-2 py-0.5 border rounded disabled:opacity-40" data-testid="boq-lines-next">Next</button>
      </div>
      {isDraft && !canAdd && !canManage && (
        <div className="text-[10px] text-slate-400">Draft lines can be modified by users holding the Project BOQ create / edit permissions.</div>
      )}
      {!isDraft && (
        <div className="text-[10px] text-slate-400" data-testid="boq-lines-immutable-note">
          {version.status === "Submitted"
            ? "Submitted versions are locked until approved or rejected."
            : `${version.status} versions are never edited in place — use Revise to create a new Draft.${canScope ? " Exception: Client Scope may be flipped in place (operational classification only — values untouched, every change audited)." : ""}`}
        </div>
      )}
    </div>
  );
}

function ComparePanel({ projectId, version, baseline, onClose }) {
  const [diff, setDiff] = useState(null);
  useEffect(() => {
    api.get(`/projects/${projectId}/boq/versions/${version.id}/compare/${baseline.id}`)
      .then(r => setDiff(r.data)).catch(e => { toast.error(e?.response?.data?.detail || "Compare failed"); onClose(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, version.id, baseline.id]);
  if (!diff) return null;
  return (
    <div className="border border-indigo-300 rounded-lg bg-white p-4 space-y-2" data-testid="boq-compare-panel">
      <div className="flex items-center justify-between">
        <div className="text-sm font-bold text-slate-900">
          Compare — V{version.version_number} vs baseline V{baseline.version_number}
        </div>
        <button data-testid="boq-compare-close" onClick={onClose} className="text-slate-500 hover:text-slate-800"><X size={16} /></button>
      </div>
      <div className="grid md:grid-cols-3 gap-3 text-xs">
        <div>
          <div className="font-bold text-emerald-700 mb-1">Added ({diff.added.length})</div>
          {diff.added.map((l, i) => <div key={i} className="truncate" title={l.name}>+ {l.name}</div>)}
        </div>
        <div>
          <div className="font-bold text-rose-700 mb-1">Removed ({diff.removed.length})</div>
          {diff.removed.map((l, i) => <div key={i} className="truncate" title={l.name}>− {l.name}</div>)}
        </div>
        <div>
          <div className="font-bold text-amber-700 mb-1">Changed ({diff.changed.length})</div>
          {diff.changed.map((c, i) => (
            <div key={i} className="mb-1">
              <div className="truncate font-medium" title={c.name}>{c.name}</div>
              {Object.entries(c.diffs).map(([f, v]) => (
                <div key={f} className="text-slate-500 pl-2">{f}: {String(v.from ?? "—")} → {String(v.to ?? "—")}</div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SalesInvoicesSection({ projectId }) {
  const [rows, setRows] = useState(null);
  const [denied, setDenied] = useState(false);
  useEffect(() => {
    api.get(`/projects/${projectId}/boq/sales-invoices`)
      .then(r => setRows(r.data.rows || []))
      .catch(e => { if (e?.response?.status === 403) setDenied(true); setRows([]); });
  }, [projectId]);
  if (denied) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-2" data-testid="boq-si-section">
      <div className="text-sm font-bold text-slate-900">Sales Invoices — this Project <span className="text-[10px] font-normal text-slate-500">(read-only · existing SI module is the source of truth)</span></div>
      {rows === null ? <div className="text-xs text-slate-500">Loading…</div> : rows.length === 0 ? (
        <div className="text-xs text-slate-500">No Sales Invoices recorded for this project yet.</div>
      ) : (
        <>
        <div className="hidden md:block overflow-x-auto max-h-72 overflow-y-auto border border-slate-200 rounded">
          <table className="w-full min-w-[820px] text-xs">
            <thead className="sticky top-0 bg-slate-100">
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-600">
                <th className="px-2 py-1.5">Invoice No</th>
                <th className="px-2 py-1.5">Date</th>
                <th className="px-2 py-1.5">Status</th>
                <th className="px-2 py-1.5 text-right">Taxable</th>
                <th className="px-2 py-1.5 text-right">GST</th>
                <th className="px-2 py-1.5 text-right">Total</th>
                <th className="px-2 py-1.5 text-right">CN</th>
                <th className="px-2 py-1.5 text-right">DN</th>
                <th className="px-2 py-1.5 text-right">Net Invoiced</th>
                <th className="px-2 py-1.5">Payment Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(si => (
                <tr key={si.id} className="border-t border-slate-100" data-testid={`boq-si-${si.invoice_number}`}>
                  <td className="px-2 py-1 font-mono text-[11px]">{si.invoice_number}</td>
                  <td className="px-2 py-1">{formatDate(si.invoice_date)}</td>
                  <td className="px-2 py-1 text-[10px]">{si.status}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{formatINR(si.taxable_value)}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{formatINR(si.gst_value)}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{formatINR(si.total_value)}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{formatINR(si.credit_note_total)}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{formatINR(si.debit_note_total)}</td>
                  <td className="px-2 py-1 text-right tabular-nums font-bold">{formatINR(si.net_invoiced_value)}</td>
                  <td className="px-2 py-1" data-testid={`boq-si-paystatus-${si.invoice_number}`}>
                    {si.payment_status
                      ? <span className="inline-block px-1.5 py-0.5 text-[9px] uppercase font-bold rounded border bg-sky-50 text-sky-700 border-sky-200">{si.payment_status}</span>
                      : <span className="text-slate-400 text-[10px]">Not Set</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile: stacked SI cards */}
        <div className="md:hidden max-h-72 overflow-y-auto border border-slate-200 rounded divide-y divide-slate-100" data-testid="boq-si-cards">
          {rows.map(si => (
            <div key={si.id} className="p-3 space-y-1" data-testid={`boq-si-card-${si.invoice_number}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-[11px] font-bold">{si.invoice_number}</span>
                <span className="text-[10px] text-slate-500">{si.status}</span>
                {si.payment_status && (
                  <span className="inline-block px-1.5 py-0.5 text-[9px] uppercase font-bold rounded border bg-sky-50 text-sky-700 border-sky-200">{si.payment_status}</span>
                )}
                <span className="ml-auto text-[10px] text-slate-400">{formatDate(si.invoice_date)}</span>
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px]">
                <span className="text-slate-500">Taxable: <b className="tabular-nums">{formatINR(si.taxable_value)}</b></span>
                <span className="text-slate-500">GST: <b className="tabular-nums">{formatINR(si.gst_value)}</b></span>
                <span className="text-slate-500">Total: <b className="tabular-nums">{formatINR(si.total_value)}</b></span>
                <span className="text-slate-500">CN/DN: <b className="tabular-nums">{formatINR(si.credit_note_total)} / {formatINR(si.debit_note_total)}</b></span>
                <span className="text-slate-500 col-span-2">Net Invoiced: <b className="tabular-nums">{formatINR(si.net_invoiced_value)}</b></span>
              </div>
            </div>
          ))}
        </div>
        </>
      )}
    </div>
  );
}

function AuditSection({ projectId }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (open) api.get(`/projects/${projectId}/boq/audit`).then(r => setRows(r.data.rows || [])).catch(() => setRows([]));
  }, [open, projectId]);
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="boq-audit-section">
      <button data-testid="boq-audit-toggle" onClick={() => setOpen(o => !o)} className="text-sm font-bold text-slate-900 inline-flex items-center gap-1.5">
        <History size={14} /> Activity Trail {open ? "▾" : "▸"}
      </button>
      {open && (
        <div className="mt-2 max-h-60 overflow-y-auto space-y-1">
          {rows.length === 0 && <div className="text-xs text-slate-500">No activity yet.</div>}
          {rows.map(r => (
            <div key={r.id} className="text-[11px] text-slate-600 border-t border-slate-100 py-1">
              <span className="font-mono text-slate-400">{formatDate(r.at)}</span>
              {" · "}<span className="font-bold">{r.action}</span>{" · "}{r.actor}
              {r.reason ? <span className="text-rose-700"> — {r.reason}</span> : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProjectBoqTab({ project, onClose }) {
  const pid = project.id;
  const { hasPerm } = useAuth();
  const [data, setData] = useState(null);
  const [forbidden, setForbidden] = useState("");
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [linesFor, setLinesFor] = useState(null);
  const [compareFor, setCompareFor] = useState(null);
  const [busyId, setBusyId] = useState("");

  const load = useCallback(() => {
    api.get(`/projects/${pid}/boq/summary`)
      .then(r => { setData(r.data); setForbidden(""); })
      .catch(e => setForbidden(e?.response?.data?.detail || "Scope & BOQ is not available"));
  }, [pid]);
  useEffect(() => { load(); }, [load]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const r = await api.post(`/projects/${pid}/boq/uploads`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
      toast.success("File validated — review the preview below");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const submitVersion = async (v) => {
    if (!window.confirm(`Submit ${v.boq_number} for approval?`)) return;
    setBusyId(v.id);
    try {
      const r = await api.post(`/projects/${pid}/boq/versions/${v.id}/submit`);
      toast.success(r.data?.auto_approved
        ? "Submitted & auto-approved — you are the configured checker"
        : "Submitted for approval");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Submit failed"); }
    finally { setBusyId(""); }
  };

  const reviseVersion = async (v) => {
    if (!window.confirm(`Create a new editable Draft cloned from ${v.boq_number}?\nThe original version and its line references stay unchanged.`)) return;
    setBusyId(v.id);
    try {
      const r = await api.post(`/projects/${pid}/boq/versions/${v.id}/revise`);
      toast.success(r.data.already_exists
        ? `Open Draft ${r.data.boq_number} already exists for this version`
        : `Draft ${r.data.boq_number} created — edit its lines, then submit for approval`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Revise failed"); }
    finally { setBusyId(""); }
  };

  const createManualDraft = async () => {
    if (!window.confirm("Create a blank Draft BOQ version for manual line entry?")) return;
    try {
      const r = await api.post(`/projects/${pid}/boq/versions/manual-draft`);
      toast.success(`Draft ${r.data.boq_number} created — add lines manually`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create draft"); }
  };

  if (forbidden) {
    return (
      <div className="p-6" data-testid="boq-forbidden">
        <div className="flex items-center gap-2 text-amber-800 bg-amber-50 border border-amber-300 rounded-lg px-4 py-3 text-sm">
          <ShieldAlert size={16} /> {forbidden}
        </div>
      </div>
    );
  }

  const baseline = data?.current_baseline || null;
  const commercial = !!data?.commercials_visible;

  // Shared version actions — desktop table cell AND mobile card (m = testid infix).
  const versionActions = (v, m = "") => (
    <>
      <button data-testid={`boq-lines-${m}${v.version_number}`} onClick={() => { setCompareFor(null); setLinesFor(v); }}
        title="View lines" className="text-[#0B1F3A] hover:underline mr-2 font-bold inline-flex items-center gap-0.5"><Eye size={11} /> Lines</button>
      {(v.status === "Draft" || v.status === "Rejected") && hasPerm("project_boq", "edit") && (
        <button data-testid={`boq-submit-${m}${v.version_number}`} disabled={busyId === v.id} onClick={() => submitVersion(v)}
          title="Submit for approval" className="text-emerald-700 hover:underline mr-2 font-bold inline-flex items-center gap-0.5"><Send size={11} /> Submit</button>
      )}
      {["Approved", "Rejected", "Superseded"].includes(v.status) && hasPerm("project_boq", "create") && (
        <button data-testid={`boq-revise-${m}${v.version_number}`} disabled={busyId === v.id} onClick={() => reviseVersion(v)}
          title="Clone into a new editable Draft — this version stays unchanged" className="text-amber-700 hover:underline mr-2 font-bold inline-flex items-center gap-0.5"><CopyPlus size={11} /> Revise</button>
      )}
      {hasPerm("project_boq", "export") && (
        <>
          <button data-testid={`boq-export-${m}${v.version_number}`}
            onClick={() => blobDownload(`/projects/${pid}/boq/versions/${v.id}/export`, `${(v.boq_number || "boq").replace(/\//g, "_")}.xlsx`)}
            title="Export Excel" className="text-slate-700 hover:underline mr-2 inline-flex items-center gap-0.5"><FileSpreadsheet size={11} /> Export</button>
          <button data-testid={`boq-original-${m}${v.version_number}`}
            onClick={() => blobDownload(`/projects/${pid}/boq/versions/${v.id}/file`, v.source_file_name || "boq_original.xlsx")}
            title="Download the original uploaded file" className="text-slate-500 hover:underline mr-2 inline-flex items-center gap-0.5"><FileDown size={11} /> Original</button>
        </>
      )}
      {baseline && v.id !== baseline.id && (
        <button data-testid={`boq-compare-${m}${v.version_number}`} onClick={() => { setLinesFor(null); setCompareFor(v); }}
          title="Compare with the approved baseline" className="text-indigo-700 hover:underline inline-flex items-center gap-0.5"><GitCompareArrows size={11} /> Compare</button>
      )}
    </>
  );

  return (
    <div className="p-4 space-y-4" data-testid="boq-tab">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-lg font-bold text-slate-900">Scope &amp; BOQ — {project.project_name}</div>
          <div className="text-xs text-slate-500">{project.project_code} · {project.client_name}</div>
        </div>
        <div className="flex gap-2">
          <button data-testid="boq-download-template" onClick={() => blobDownload("/boq/template", "boq_template.xlsx")}
            className="px-3 py-1.5 text-xs font-bold border border-slate-300 text-slate-700 bg-white rounded hover:bg-slate-50 inline-flex items-center gap-1.5">
            <Download size={13} /> Template
          </button>
          {hasPerm("project_boq", "create") && (
            <button data-testid="boq-manual-draft-btn" onClick={createManualDraft}
              className="px-3 py-1.5 text-xs font-bold border border-emerald-400 text-emerald-800 bg-emerald-50 rounded hover:bg-emerald-100 inline-flex items-center gap-1.5">
              <Plus size={13} /> Blank Draft
            </button>
          )}
          {hasPerm("project_boq", "create") && (
            <label className={`px-3 py-1.5 text-xs font-bold bg-[#F97316] text-white rounded hover:bg-[#ea6a0a] inline-flex items-center gap-1.5 cursor-pointer ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
              <Upload size={13} /> {uploading ? "Validating…" : "Upload BOQ (.xlsx)"}
              <input data-testid="boq-upload-input" type="file" accept=".xlsx" className="hidden" onChange={onFile} />
            </label>
          )}
          <button data-testid="boq-refresh" onClick={load} title="Refresh"
            className="px-2 py-1.5 border border-slate-300 rounded text-slate-600 hover:bg-slate-50"><RefreshCw size={13} /></button>
          {onClose && (
            <button data-testid="boq-close" onClick={onClose}
              className="px-2 py-1.5 border border-slate-300 rounded text-slate-600 hover:bg-slate-50"><X size={13} /></button>
          )}
        </div>
      </div>

      {!commercial && data && (
        <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded px-3 py-1.5" data-testid="boq-no-commercials-note">
          Commercial fields (rates, taxes, values) are hidden — you do not hold the BOQ Commercials permission.
        </div>
      )}

      {preview && (
        <UploadPreview projectId={pid} preview={preview}
          onCommitted={() => { setPreview(null); load(); }}
          onDiscarded={() => setPreview(null)} />
      )}

      <div className="bg-white border border-emerald-300 rounded-lg p-4" data-testid="boq-baseline-card">
        <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold mb-1">Current Approved Baseline</div>
        {baseline ? (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-3">
              <span className="font-mono text-sm font-bold text-slate-900">{baseline.boq_number}</span>
              <Badge status="Approved" />
              <span className="text-xs text-slate-500">approved {formatDate(baseline.approved_at)} by {baseline.approved_by}</span>
            </div>
            <SummaryTiles summary={baseline.summary} commercial={commercial} />
          </div>
        ) : (
          <div className="text-xs text-slate-500">No approved baseline yet — upload a BOQ, commit a draft and submit it for approval.</div>
        )}
      </div>

      {linesFor && <LinesPanel projectId={pid} version={linesFor} onClose={() => setLinesFor(null)} onDataChanged={load} />}
      {compareFor && baseline && compareFor.id !== baseline.id && (
        <ComparePanel projectId={pid} version={compareFor} baseline={baseline} onClose={() => setCompareFor(null)} />
      )}

      <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-2">
        <div className="text-sm font-bold text-slate-900">Version History</div>
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full min-w-[720px] text-xs" data-testid="boq-versions-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-600 bg-slate-100">
                <th className="px-2 py-1.5">Version</th>
                <th className="px-2 py-1.5">BOQ Number</th>
                <th className="px-2 py-1.5">Status</th>
                <th className="px-2 py-1.5 text-right">Lines</th>
                {commercial && <th className="px-2 py-1.5 text-right">Scope Value (ex GST)</th>}
                <th className="px-2 py-1.5">Created</th>
                <th className="px-2 py-1.5">Remarks</th>
                <th className="px-2 py-1.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(data?.versions || []).map(v => (
                <tr key={v.id} className="border-t border-slate-100" data-testid={`boq-version-row-${v.version_number}`}>
                  <td className="px-2 py-1.5 font-bold">V{v.version_number}</td>
                  <td className="px-2 py-1.5 font-mono text-[11px]">{v.boq_number}</td>
                  <td className="px-2 py-1.5"><Badge status={v.status} /></td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{v.line_count}</td>
                  {commercial && <td className="px-2 py-1.5 text-right tabular-nums">{formatINR(v.summary?.scope_value_ex_gst || 0)}</td>}
                  <td className="px-2 py-1.5 text-slate-500">{formatDate(v.created_at)} · {v.created_by}</td>
                  <td className="px-2 py-1.5 max-w-[180px] truncate text-rose-700" title={v.approval_remarks}>{v.status === "Rejected" ? v.approval_remarks : ""}</td>
                  <td className="px-2 py-1.5 whitespace-nowrap">{versionActions(v)}</td>
                </tr>
              ))}
              {data && (data.versions || []).length === 0 && (
                <tr><td colSpan={commercial ? 8 : 7} className="px-2 py-4 text-center text-slate-400">No BOQ versions yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile: stacked version cards */}
        <div className="md:hidden divide-y divide-slate-100" data-testid="boq-versions-cards">
          {(data?.versions || []).map(v => (
            <div key={v.id} className="py-3 space-y-1.5" data-testid={`boq-version-card-${v.version_number}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-xs">V{v.version_number}</span>
                <span className="font-mono text-[10px]">{v.boq_number}</span>
                <Badge status={v.status} />
                <span className="ml-auto text-[11px] tabular-nums">{v.line_count} lines</span>
              </div>
              {commercial && (
                <div className="text-[11px] text-slate-600">Scope Value (ex GST): <b className="tabular-nums">{formatINR(v.summary?.scope_value_ex_gst || 0)}</b></div>
              )}
              <div className="text-[10px] text-slate-400 break-all">{formatDate(v.created_at)} · {v.created_by}</div>
              {v.status === "Rejected" && v.approval_remarks && (
                <div className="text-[10px] text-rose-700 break-words">{v.approval_remarks}</div>
              )}
              <div className="flex flex-wrap items-center gap-y-1.5 pt-0.5">{versionActions(v, "m-")}</div>
            </div>
          ))}
          {data && (data.versions || []).length === 0 && (
            <div className="py-4 text-center text-slate-400 text-xs">No BOQ versions yet.</div>
          )}
        </div>
      </div>

      <SalesInvoicesSection projectId={pid} />
      <AuditSection projectId={pid} />
    </div>
  );
}
